import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PlusOutlined } from "@ant-design/icons";
import { Row, Skeleton, Space, Typography } from "antd";
import snakecaseKeys from "snakecase-keys";
import { AuthInfo } from "stores/slices/auth";
import { setHeaderTitle, setSubHeaderTitle } from "stores/slices/layout";
import {
  clearSubscriptionDetail,
  clearSubscriptionList,
  doCancelDowngradeSubscription,
  doDeleteSubscription,
  doGetSubscriptionAll,
  doGetSubscriptionDetail,
  doGetUseSubscription,
  getAllSubscriptions,
  getCurrentSubscription,
  getIsLoading,
} from "stores/slices/subscriptions";
import {
  getWorkspaceInfo,
  isSaving,
  updateWorkspace,
  WorkspaceInfo,
} from "stores/slices/workspace";

import AppButton from "components/button";
import ModalForm from "components/forms/components/modalForm";
import SubscriptionCard from "components/subscriptions/subscriptionCard";
import SubscriptionForm from "components/subscriptions/subscriptionForm";

import { USER_TYPE, WORKSPACE_USER_TYPE } from "../../constants/appConstant";

const FORM_VALUE = {
  ADD: "ADD",
  EDIT: "EDIT",
  VIEW: "VIEW",
};

const SubscriptionPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [dataList, setDataList] = useState([]);
  const [textConfirm, setTextConfirm] = useState("");
  const [isSelected, setId] = useState();
  const [copy, setCopy] = useState({});
  let list = useSelector(getAllSubscriptions);
  const authUser = useSelector(AuthInfo);
  const isLoading = useSelector(getIsLoading);
  const currentSubscription = useSelector(getCurrentSubscription);
  const workspaceInfo = useSelector(WorkspaceInfo);
  const { t } = useTranslation();

  const params = useParams();
  const { id: workspaceId } = params;
  const location = useLocation();
  const isSave = useSelector(isSaving);

  // const [subscriptionDetail, setSubscriptionDetail] = useState({});

  useEffect(() => {
    if (location.state && location.state.name) {
      dispatch(setHeaderTitle(["subscription", "workspace"]));
      dispatch(setSubHeaderTitle(location.state.name));
    } else {
      dispatch(setHeaderTitle("subscription"));
    }
  }, []);

  useEffect(() => {
    if (workspaceId && location.state && location.state.subscriptionId) {
      // handleGetSubscriptionDetail(location.state.subscriptionId);
      handleGetWorkspaceDetail(workspaceId);
    }
    if (
      (location.state && !location.state.subscriptionId) ||
      (workspaceId && !location.state)
    ) {
      navigate("/admin/workspaces");
    }
  }, []);

  useEffect(() => {
    if (list) {
      setDataList(list);
      if (authUser.type === USER_TYPE.USER) {
        const index = list.findIndex(
          i => i.id === currentSubscription.subscriptionPackageId
        );
        if (index === -1 && currentSubscription.subscriptionPackageId) {
          handleLoadSubscriptionHidden().then(res => {
            list = [...list, { ...res.payload }];
            setDataList(list);
          });
        }
      }
    }
  }, [list]);

  useEffect(() => {
    if (authUser && authUser.id) {
      if (authUser.type === USER_TYPE.SUPER_ADMIN) {
        if (workspaceId) {
          handleLoadAllSubscription(workspaceId);
          dispatch(doGetUseSubscription(workspaceId));
        } else {
          handleLoadAllSubscription();
        }
      } else {
        if (workspaceId) {
          dispatch(doGetUseSubscription(workspaceId));
        } else {
          dispatch(doGetUseSubscription());
        }
        if (workspaceId) {
          handleLoadAllSubscription(workspaceId);
          // dispatch(doGetUseSubscription(workspaceId));
        } else if (authUser.workspaceId) {
          handleLoadAllSubscription(authUser.workspaceId);
        } else {
          dispatch(clearSubscriptionList());
        }
      }
    }
  }, [authUser, workspaceId]);

  const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "20px",
  };
  const titleStyle = {
    color: "rgba(17, 25, 39, 1)",
    fontSize: "24px",
    fontWeight: "700",
    lineHeight: "32px",
    letterSpacing: "-0.005em",
  };

  const handleGetWorkspaceDetail = async workspaceId => {
    await dispatch(getWorkspaceInfo(workspaceId));
  };

  // const handleGetSubscriptionDetail = async subscriptionId => {
  //   const { payload } = await dispatch(doGetSubscriptionDetail(subscriptionId));
  //   // setSubscriptionDetail(payload);
  // };

  const handleLoadSubscriptionHidden = async () => {
    return await dispatch(
      doGetSubscriptionDetail(currentSubscription.subscriptionPackageId)
    );
  };
  const handleLoadAllSubscription = async workspaceId => {
    await dispatch(doGetSubscriptionAll({ workspaceId: workspaceId }));
  };
  const handleOpenCloseModal = action => {
    setOpenModal(action);
  };
  const handleOnUpdate = async id => {
    await dispatch(clearSubscriptionDetail());
    setOpenModal(FORM_VALUE.EDIT);
    await dispatch(doGetSubscriptionDetail(id));
  };

  const handleOnView = async id => {
    await dispatch(clearSubscriptionDetail());
    setOpenModal(FORM_VALUE.VIEW);
    await dispatch(doGetSubscriptionDetail(id));
  };

  const handleCopySubscription = async item => {
    toast.success(t("text.copySuccess"));
    setOpenModal("");
    setCopy(item);
    setOpenModal(FORM_VALUE.ADD);
  };
  const handleOnDelete = item => {
    setOpenModalDelete(true);
    setTextConfirm(item.name);
    setId(item.id);
  };
  const handleOnSelected = item => {
    if (workspaceId) {
      if (authUser.type === USER_TYPE.SUPER_ADMIN) {
        handleSubscriptionForWorkspace({
          ...workspaceInfo,
          subscriptionId: item.id,
        });
      } else {
        handleSubmitSelect(item.id);
      }
    } else {
      handleSubmitSelect(item.id);
    }
  };

  const handleSubscriptionForWorkspace = async data => {
    const request = {
      address: data.address,
      comment: data.comment,
      contactEmail: data.contactEmail,
      contactName: data.contactName,
      currency: data?.subscriptionPackage?.currency,
      id: data.id,
      invoiceEmail: data.invoiceEmail,
      name: data.name,
      phoneNumber: data.phoneNumber,
      subscriptionPackageId: data.subscriptionId,
    };
    const { payload } = await dispatch(updateWorkspace(snakecaseKeys(request)));

    if (payload && payload.status === 200) {
      // handleGetSubscriptionDetail(data.subscriptionId);
      handleGetWorkspaceDetail(workspaceId);
      handleLoadAllSubscription(workspaceId);
      dispatch(doGetUseSubscription(workspaceId));
    }
  };

  const handleSubmitDelete = async () => {
    await dispatch(doDeleteSubscription(isSelected));
    setOpenModalDelete(false);
  };
  const handleSubmitSelect = async id => {
    if (workspaceId) {
      navigate(`/admin/payment/${id}`, {
        state: {
          workspaceId: workspaceId,
        },
      });
    } else {
      navigate(`/admin/payment/${id}`);
    }
  };
  const handleCancelModal = async () => {
    setOpenModal("");
    setCopy({});
    await dispatch(clearSubscriptionDetail());
  };

  const handleOpenAddNew = async () => {
    await dispatch(clearSubscriptionDetail());
    handleOpenCloseModal(FORM_VALUE.ADD);
  };
  const handleDownGrade = async () => {
    const request = {
      workspaceId: currentSubscription.workspaceId,
      subscriptionPackageId: currentSubscription.subscriptionPackageId,
    };
    await dispatch(doCancelDowngradeSubscription(request));
    if (authUser?.type === USER_TYPE.SUPER_ADMIN) {
      dispatch(doGetUseSubscription(workspaceId));
    }
  };

  return (
    <div className="page">
      <div style={headerStyle}>
        <Typography style={titleStyle}>
          {t("subscriptions.title.subscription")}
        </Typography>
        {authUser.type === USER_TYPE.SUPER_ADMIN && (
          <AppButton
            label={t("button.add")}
            color={"primary"}
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenAddNew}
            style={{
              minWidth: "auto",
            }}
          />
        )}
      </div>
      {!workspaceId ? (
        <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 20]}>
          {isLoading && (
            <Space
              direction="vertical"
              style={{ width: "100%", height: "100%" }}
            >
              <Skeleton active /> <Skeleton active /> <Skeleton active />
            </Space>
          )}
          {!isLoading &&
            dataList.length > 0 &&
            (authUser.type === USER_TYPE.SUPER_ADMIN
              ? dataList.map((item, index) => {
                  return (
                    <SubscriptionCard
                      key={index}
                      item={item}
                      userType={authUser.type}
                      onUpdate={() => {
                        handleOnUpdate(item.id);
                      }}
                      onView={() => {
                        handleOnView(item.id);
                      }}
                      onCopy={() => {
                        handleCopySubscription(item);
                      }}
                      onDelete={() => {
                        handleOnDelete(item);
                      }}
                      onSelected={() => {
                        handleOnSelected(item);
                      }}
                    />
                  );
                })
              : dataList.map((item, index) => {
                  if (authUser.workspaceRole === WORKSPACE_USER_TYPE.ADMIN) {
                    if (
                      !item.hide ||
                      item.id === currentSubscription.subscriptionPackageId
                    ) {
                      return (
                        <SubscriptionCard
                          key={index}
                          item={item}
                          isSelected={
                            !!(
                              currentSubscription.subscriptionPackageId &&
                              item.id ===
                                currentSubscription.subscriptionPackageId
                            )
                          }
                          userType={authUser.type}
                          onView={() => {
                            handleOnView(item.id);
                          }}
                          onSelected={() => {
                            handleOnSelected(item);
                          }}
                          isDownGrade={
                            currentSubscription.downgrade &&
                            item.id === currentSubscription.downgradePackageId
                          }
                          onCancelDownGrade={() => {
                            handleDownGrade();
                          }}
                        />
                      );
                    }
                  } else {
                    if (item.id === currentSubscription.subscriptionPackageId) {
                      return (
                        <SubscriptionCard
                          key={index}
                          item={item}
                          isSelected={true}
                          userType={authUser.type}
                          onView={() => {
                            handleOnView(item.id);
                          }}
                        />
                      );
                    }
                  }
                }))}
        </Row>
      ) : authUser.type === USER_TYPE.SUPER_ADMIN ? (
        <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 20]}>
          {isLoading && (
            <Space
              direction="vertical"
              style={{ width: "100%", height: "100%" }}
            >
              <Skeleton active /> <Skeleton active /> <Skeleton active />
            </Space>
          )}
          {!isLoading &&
            dataList.map((item, index) => {
              if (
                !item.hide ||
                item.id === currentSubscription.subscriptionPackageId
              ) {
                return (
                  <SubscriptionCard
                    key={index}
                    item={item}
                    isSelected={
                      !!(
                        currentSubscription.subscriptionPackageId &&
                        item.id === currentSubscription.subscriptionPackageId
                      )
                    }
                    userType={authUser.type}
                    isAdminSelect={true}
                    // onUpdate={() => {
                    //   handleOnUpdate(item.id);
                    // }}
                    // onView={() => {
                    //   handleOnView(item.id);
                    // }}
                    onSelected={() => {
                      handleOnSelected(item);
                    }}
                    isDownGrade={
                      currentSubscription.downgrade &&
                      item.id === currentSubscription.downgradePackageId
                    }
                    onCancelDownGrade={() => {
                      handleDownGrade();
                    }}
                    isLoading={isSave}
                    // onDelete={() => {
                    //   handleOnDelete(item);
                    // }}
                  />
                );
              }
              // if (authUser.workspaceRole === WORKSPACE_USER_TYPE.ADMIN) {

              // } else {
              //   if (item.id === subscriptionDetail.subscriptionPackageId) {
              //     return (
              //       <SubscriptionCard
              //         key={index}
              //         item={item}
              //         isSelected={true}
              //         userType={authUser.type}
              //         onView={() => {
              //           handleOnView(item.id);
              //         }}
              //       />
              //     );
              //   }
              // }
            })}
        </Row>
      ) : (
        <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 20]}>
          {isLoading && (
            <Space
              direction="vertical"
              style={{ width: "100%", height: "100%" }}
            >
              <Skeleton active /> <Skeleton active /> <Skeleton active />
            </Space>
          )}
          {!isLoading &&
            dataList.map((item, index) => {
              if (
                !item.hide ||
                item.id === currentSubscription.subscriptionPackageId
              ) {
                return (
                  <SubscriptionCard
                    key={index}
                    item={item}
                    isSelected={
                      !!(
                        currentSubscription.subscriptionPackageId &&
                        item.id === currentSubscription.subscriptionPackageId
                      )
                    }
                    userType={authUser.type}
                    onView={() => {
                      handleOnView(item.id);
                    }}
                    onSelected={() => {
                      handleOnSelected(item);
                    }}
                    isDownGrade={
                      currentSubscription.downgrade &&
                      item.id === currentSubscription.downgradePackageId
                    }
                    onCancelDownGrade={() => {
                      handleDownGrade();
                    }}
                    isLoading={isLoading}
                  />
                );
              }
            })}
          {/* <SubscriptionCard
            key={subscriptionDetail.id}
            item={subscriptionDetail}
            isSelected={true}
            userType={authUser.type}
            onUpdate={() => {
              handleOnUpdate(subscriptionDetail.id);
            }}
            onView={() => {
              handleOnView(subscriptionDetail.id);
            }}
            onSelected={() => {
              handleOnSelected(subscriptionDetail);
            }}
            isView={true}
          /> */}
        </Row>
      )}
      <ModalForm
        title={
          openModal === FORM_VALUE.ADD
            ? t("subscriptions.addNewSubscription")
            : openModal === FORM_VALUE.EDIT
            ? t("subscriptions.updateSubscription")
            : t("subscriptions.viewSubscription")
        }
        isOpen={openModal}
        onCancel={handleCancelModal}
        customWidth={800}
        footer={false}
        destroyOnClose={true}
        maskClosable={false}
      >
        <SubscriptionForm
          openModal={openModal}
          onCancel={handleCancelModal}
          onCopy={handleCopySubscription}
          cloneSubscription={copy}
          userType={authUser.type}
          setOpenModal={setOpenModal}
          workspaceId={authUser.workspaceId}
        />
      </ModalForm>
      <ModalForm
        title={t("subscriptions.deleteSubscription")}
        isOpen={openModalDelete}
        onCancel={() => setOpenModalDelete(false)}
        onSubmit={handleSubmitDelete}
        maskClosable={false}
      >
        {t("subscriptions.confirmDelete", { value: textConfirm })}
      </ModalForm>
    </div>
  );
};

export default SubscriptionPage;
